// src/components/optimization-score/PriceBenchmarkSection.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { formatDecimal, formatInteger, numberLocale } from '@/lib/language';
import type { PriceBenchmarkSummary } from '@/hooks/usePriceBenchmarkSummary';

/**
 * Kapitel 2 des Feed-Check-Reports (Spec §3.5). Rein darstellend: alle Zahlen
 * kommen fertig aggregiert aus der RPC price_benchmark_summary. Diese
 * Komponente rechnet nur Anzeige-Prozente und formatiert Preise.
 *
 * Diverging-Bars: Nulllinie bei 50 % der Spurbreite, Ausschlag proportional
 * zur Abweichung, gekappt bei +/-25 % (SCALE_MAX) — ein 40-%-Ausreisser soll
 * die Spur nicht sprengen, die Zahl daneben traegt den echten Wert.
 */
const SCALE_MAX = 25;

/**
 * Spec §3.5 Iteration 2: die Ausreisser-Liste laesst sich auf ein Segment der
 * Preisverteilung filtern. Reiner Client-State (kein Refetch, keine URL- und
 * keine localStorage-Persistenz) — die RPC liefert alle drei Listen in EINEM
 * Call, der Klick ist damit ein Umschalter ohne Ladezustand. `null` ist der
 * Ausgangszustand und rendert exakt das Verhalten von vor dieser Iteration.
 *
 * Iteration 3 (Review-Welle Barrierefreiheit): das Bedienelement sind die
 * Chips UNTER dem Balken, nicht mehr die Balken-Segmente selbst. Grund ist die
 * Zielgroesse: das kleinste Segment eines echten Feeds mass 22,86 px, der
 * gepinnte 0,32-Prozent-Fall 0,94 px, und die Abstands-Ausnahme von WCAG 2.2
 * SC 2.5.8 greift wegen des `gap-0.5` zwischen den Segmenten nicht. Dazu war
 * der Fokusring auf der roten und der gruenen Fuellung mit 1,07:1 bis 1,54:1
 * unsichtbar (gefordert sind 3:1), weil ein `ring-inset` als einzigen Nachbarn
 * die Fuellung selbst hat. Die Balken-Segmente bleiben als Zeiger-Abkuerzung
 * klickbar, sind aber weder Tabstopp noch Screenreader-Ziel.
 *
 * Iteration 4 (zweite Review-Welle): die PROZENTANTEILE sind aus dem Balken in
 * die Chip-Beschriftung gewandert, der Balken ist eine reine Flaechengrafik
 * ohne Text. Das loest drei Befunde mit EINEM Umbau:
 *   1. Die weisse Balkenschrift verfehlte 4,5:1 deutlich, gemessen 3,78:1 auf
 *      `--destructive` hell, 2,75:1 dunkel und 1,92:1 auf `--success` im
 *      Dunkelmodus. Der Text ist 11 px und halbfett, also kein "large text".
 *      `src/index.css` dokumentiert genau diesen Fall bereits fuer `--success`,
 *      und `src/lib/contrast.test.ts` haelt als Hausregel fest, dass
 *      `--success` und `--destructive` keine Schrift-Token sind. Ohne Text
 *      faellt der Balken aus WCAG 1.4.3 heraus, statt die Regel zu brechen.
 *   2. Mit `aria-hidden` auf den Segmenten war `within_count` in KEINEM
 *      zugaenglichen Text mehr vorhanden (WCAG 1.1.1). Ueber die Chips traegt
 *      jeder der drei Anteile wieder eine Textfassung, und zwar dieselbe
 *      Zeichenkette, die das Bedienelement beschriftet.
 *   3. `aria-hidden` auf einem `<button>` ist laut ARIA unzulaessig, und ein
 *      Button nimmt beim Mausklick trotzdem DOM-Fokus an. Die Segmente sind
 *      jetzt reine `<div>`-Flaechen mit Klick-Handler.
 *
 * Iteration 5 (dritte Review-Welle): der Textumzug aus Iteration 4 hat das
 * MITTLERE Segment unsichtbar gemacht. Seine Fuellung `hsl(var(--muted))` kommt
 * gegen die Kartenflaeche auf 1,10:1 (hell) und 1,34:1 (dunkel). Erkennbar war
 * es vorher nur an seiner Beschriftung, seither las sich der Balken, als klaffe
 * zwischen dem roten und dem gruenen Teil eine Luecke. Bei realen Feeds ist das
 * der Normalfall und nicht der Randfall (golfimport: 2.895 von 7.934 Produkten
 * liegen im Marktband). Der Balken ist ein bedeutungstragendes Diagramm, seine
 * Teile brauchen 3:1 gegen ihre Umgebung (WCAG 1.4.11). Behoben ueber die
 * Umrandung der Spur, nicht ueber eine schwerere Fuellung. Begruendung mit
 * allen gemessenen Zahlen bei `BAR_TRACK`.
 */
type PriceSegment = 'above' | 'within' | 'below';

/** Reihenfolge von Balken UND Chips: teuer nach guenstig, wie die Verteilung. */
const SEGMENTS: readonly PriceSegment[] = ['above', 'within', 'below'] as const;

/**
 * Sichtbarer Chip-Text, seit Iteration 4 MIT dem Prozentanteil des Segments.
 * Derselbe Schluessel traegt den Segmentnamen in der aria-live-Meldung, damit
 * Chip und Ansage nie auseinanderlaufen.
 */
const SEG_CHIP_KEY: Record<PriceSegment, string> = {
  above: 'priceBenchmark.segChipAbove',
  within: 'priceBenchmark.segChipWithin',
  below: 'priceBenchmark.segChipBelow',
};

const SEG_LIST: Record<PriceSegment, { rowsKey: 'top_above' | 'top_within' | 'top_below'; titleKey: string }> = {
  above: { rowsKey: 'top_above', titleKey: 'priceBenchmark.topTitle' },
  within: { rowsKey: 'top_within', titleKey: 'priceBenchmark.topTitleWithin' },
  below: { rowsKey: 'top_below', titleKey: 'priceBenchmark.topTitleBelow' },
};

/**
 * Markierung des aktiven Segments IM Balken. Zwei Baender nebeneinander: eines
 * in der Hintergrund-, eines in der Vordergrundfarbe. Ein einfarbiger
 * Foreground-Ring erreichte im Dunkelmodus auf `--success` nur 1,75:1; mit zwei
 * Toenen traegt auf jeder Fuellung mindestens ein Band mehr als 3:1. `inset`,
 * weil der Balken-Container `overflow-hidden` traegt und einen aeusseren Ring
 * abschneiden wuerde.
 *
 * Exportiert, damit der Test die Markierung pinnen kann, ohne die Klasse ein
 * zweites Mal auszuschreiben.
 */
export const BAR_ACTIVE_MARK =
  'shadow-[inset_0_0_0_2px_hsl(var(--background)),inset_0_0_0_4px_hsl(var(--foreground))]';

/**
 * Spur des Segment-Balkens. `ring-1 ring-muted-foreground` ist der Fix aus
 * Iteration 5 und die Antwort auf die Frage, WELCHE Beziehung eigentlich reisst.
 *
 * Gerissen ist genau eine: Fuellung gegen die umgebende Kartenflaeche. Das
 * mittlere Segment traegt `hsl(var(--muted))` und kommt damit gegen `--card` auf
 * 1,10:1 (hell) und 1,34:1 (dunkel). Gegen seine NACHBARN ist dieselbe Fuellung
 * dagegen unauffaellig: 3,45:1 / 5,25:1 gegen `--destructive` und 4,34:1 /
 * 7,51:1 gegen `--success`, alle vier ueber den geforderten 3:1.
 *
 * Deshalb die Umrandung und nicht eine kraeftigere Fuellung. Die Gegenrechnung
 * zur Fuellungs-Variante, mit demselben Lineal:
 *   - `--muted-foreground` voll deckend erreicht zwar 4,70:1 / 5,05:1 gegen die
 *     Karte, faellt aber gegen die Nachbarn auf 1,01:1 zu `--success` und
 *     1,24:1 zu `--destructive` (hell). Das repariert eine Beziehung und zerlegt
 *     die andere: das neutrale Band waere im Hellmodus leuchtdichtegleich mit
 *     dem gruenen. Dazu waere es mit 4,70:1 genauso schwer wie Rot (3,78:1) und
 *     Gruen (4,76:1), das Marktband soll aber ruhig bleiben.
 *   - Mit Deckungsgraden verschiebt sich das Problem nur: 3:1 gegen die Karte
 *     verlangt mindestens 80 % (hell, 3,20:1) bzw. 70 % (dunkel, 3,05:1), und
 *     bei 80 % steht der Nachbarkontrast im Hellmodus bei 1,49:1 zu `--success`.
 *   - `--border` (1,41:1 / 1,76:1) und `--secondary` (1,10:1 / 1,34:1, derselbe
 *     Wert wie `--muted`) reissen die 3:1 gegen die Karte selbst.
 *
 * Die Umrandung dagegen kostet nichts an anderer Stelle: `--muted-foreground`
 * erreicht 4,70:1 gegen `--card` und 4,45:1 gegen `--background` im Hellmodus,
 * 5,05:1 bzw. 5,12:1 im Dunkelmodus. Das mittlere Segment ist damit oben und
 * unten durch die Spurkante und links/rechts durch die Kanten seiner Nachbarn
 * definiert.
 *
 * Zwei Nebenwirkungen bewusst geprueft:
 *   - `ring-1` ist in Tailwind ein AEUSSERER `box-shadow` des Elements selbst.
 *     Er folgt `rounded-lg` und wird von `overflow-hidden` nicht abgeschnitten
 *     (das klippt Kinder, nicht den eigenen Schatten) und veraendert kein
 *     Layout. `ring-inset` waere hier falsch: ein Inset-Schatten des Elternteils
 *     liegt UNTER den Kindern und verschwaende unter den Segmentfuellungen.
 *   - Der Zwei-Ton-Aktivmarker bleibt lesbar: sein aeusseres Band ist
 *     `--background`, gegen die Ringfarbe sind das 4,45:1 hell und 5,12:1
 *     dunkel. Ring und Marker verschmelzen also nicht zu einem Block.
 */
export const BAR_TRACK =
  'flex h-7 w-full gap-0.5 overflow-hidden rounded-lg ring-1 ring-muted-foreground';

/**
 * Abdeckungs-Abzeichen im Kapitelkopf. `text-foreground` statt
 * `text-muted-foreground`: die Fuellung ist `bg-muted`, und dieses Paar ist im
 * Haus bereits als Kontrastbefund abgelegt, gemessen 4,29:1 hell und 3,78:1
 * dunkel, beides unter den 4,5:1 fuer Fliesstext (`layoutHeaderContrast.test.ts`
 * haelt genau diese beiden Zahlen fest, der Header hat das Paar deshalb
 * verloren). Auf derselben Fuellung erreicht `--foreground` 16,32:1 und
 * 13,16:1. Gleiche Wahl wie die Badge-Variante `scoreSolid` in
 * `src/components/ui/badge.tsx`, die einzige neutrale Fuellpille des Hauses.
 */
export const COVERAGE_BADGE = 'text-xs text-foreground rounded-md bg-muted px-2.5 py-0.5';

/**
 * Chip-Basis. `min-h-[28px]` plus `px-2.5 py-1` haelt die Trefferflaeche in
 * beiden Richtungen ueber den 24 px aus WCAG 2.2 SC 2.5.8, `gap-2` an der Reihe
 * haelt die geforderten 8 px Abstand. Fokus nach Hausmuster aus
 * `src/components/ui/button.tsx`: aeusserer Ring mit Offset auf dem
 * Karten-Hintergrund, dort erreicht `--ring` 4,18:1 hell und 6,62:1 dunkel.
 */
export const CHIP_BASE =
  'min-h-[28px] rounded-md px-2.5 py-1 text-xs font-medium transition-colors ring-offset-background ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * Der aktive Chip traegt `border border-transparent`, obwohl er keinen
 * sichtbaren Rahmen zeigt: ohne ihn faellt beim Umschalten 1 px Rahmen weg und
 * die benachbarten Chips springen um 2 px. Kontrast der Fuellung gemessen
 * 16,95:1 hell und 17,85:1 dunkel (Pin in der Test-Datei).
 */
export const CHIP_ACTIVE = 'border border-transparent bg-foreground text-background';

/**
 * `border-muted-foreground` statt `border-border`: an dieser Umrandung wird das
 * Bedienelement ueberhaupt erst als solches erkannt, dafuer verlangt WCAG 1.4.11
 * 3:1. `--border` kam gegen die Karte auf 1,41:1 hell und 1,76:1 dunkel,
 * `--muted-foreground` auf 4,70:1 und 5,05:1.
 */
export const CHIP_INACTIVE =
  'border border-muted-foreground text-muted-foreground hover:bg-muted hover:text-foreground';

function priceLabel(micros: number, currency: string | null, language: string | undefined): string {
  return `${formatDecimal(micros / 1e6, language, 2)} ${currency ?? ''}`.trim();
}

function devInk(devPct: number): string {
  if (devPct > 5) return 'text-destructive-ink';
  if (devPct < -5) return 'text-success-ink';
  return 'text-muted-foreground';
}

function devBg(devPct: number): string {
  if (devPct > 5) return 'hsl(var(--destructive))';
  if (devPct < -5) return 'hsl(var(--success))';
  return 'hsl(var(--warning))';
}

/**
 * Farbe der Prozent-Badges in der Ausreisser-Liste. Bewusst am SEGMENT und
 * nicht am Zeilenwert: `dev_pct` kommt serverseitig auf ganze Prozent gerundet
 * an, eine Zeile mit echten 5,4 Prozent laege als 5 im neutralen Bereich und
 * waere in der Ueber-Benchmark-Liste grau statt rot. Ueber das Segment stimmt
 * die Farbe per Konstruktion mit dem Balken ueberein.
 */
function segInk(seg: PriceSegment): string {
  if (seg === 'above') return 'text-destructive-ink';
  if (seg === 'below') return 'text-success-ink';
  return 'text-muted-foreground';
}

export function PriceBenchmarkSection({ summary, chapterTotal }: { summary: PriceBenchmarkSummary; chapterTotal: 2 | 3 | 4 }) {
  const { t, i18n } = useTranslation('optimization');
  const language = i18n?.language;
  const pct = (n: number) => (summary.bench_count > 0 ? Math.round((n / summary.bench_count) * 100) : 0);
  // Finding 4 (Review H1): Sichtbarkeit haengt am COUNT, nicht am gerundeten
  // Prozent — bei 4/1240 sagte die Headline "4 Produkte", der Balken zeigte
  // aber kein rotes Segment. Breite ungerundet, Label gerundet, gerundet 0
  // wird als "<1" beschriftet statt das Segment zu verschlucken.
  const widthPct = (n: number) => (summary.bench_count > 0 ? (n / summary.bench_count) * 100 : 0);
  const pctLabel = (n: number): number | string => {
    const rounded = pct(n);
    return rounded === 0 ? '<1' : rounded;
  };
  const coveragePct = summary.product_total > 0
    ? Math.round((summary.bench_count / summary.product_total) * 100)
    : 0;
  const clamp = (dev: number) => Math.min(Math.abs(dev), SCALE_MAX) / SCALE_MAX * 50;

  const [segment, setSegment] = React.useState<PriceSegment | null>(null);
  // Klick auf das AKTIVE Segment hebt den Filter auf. Ein Umschalter braucht
  // seinen eigenen Ausstieg, der Reset-Chip ist nur der sichtbare zweite Weg.
  const toggleSegment = (seg: PriceSegment) => setSegment((cur) => (cur === seg ? null : seg));

  const segCount = (seg: PriceSegment) =>
    seg === 'within' ? summary.within_count : seg === 'below' ? summary.below_count : summary.above_count;

  // Der Filterzustand haengt an der Komponenteninstanz, die Daten wechseln aber
  // mit dem Feed. Ohne diese Ableitung ueberlebt ein Filter den Feed-Wechsel und
  // zeigt auf ein Segment, das im neuen Feed leer sein kann: sein Chip wird dann
  // gar nicht gerendert, der Filter waere unsichtbar aktiv und das Kapitel
  // oeffnete im Leerzustand. Jeder Leser unten benutzt deshalb
  // `effectiveSegment`, nie den rohen State.
  const effectiveSegment: PriceSegment | null =
    segment !== null && segCount(segment) > 0 ? segment : null;

  // Der Reset-Chip entfernt sich beim Klick selbst aus dem DOM, der Fokus fiele
  // sonst auf document.body. Nach dem Zuruecksetzen bekommt ihn der zuvor aktive
  // Segment-Chip, der weiterhin gerendert ist.
  const chipRefs = React.useRef<Record<PriceSegment, HTMLButtonElement | null>>({
    above: null,
    within: null,
    below: null,
  });
  const refocusChip = React.useRef<PriceSegment | null>(null);
  React.useEffect(() => {
    const target = refocusChip.current;
    if (target === null) return;
    refocusChip.current = null;
    chipRefs.current[target]?.focus();
  }, [segment]);

  const resetSegment = () => {
    refocusChip.current = effectiveSegment;
    setSegment(null);
  };

  const listSegment: PriceSegment = effectiveSegment ?? 'above';
  const topList = {
    rows: summary[SEG_LIST[listSegment].rowsKey],
    titleKey: SEG_LIST[listSegment].titleKey,
  };
  // Ungefiltert bleibt die Sektion bei leerer Liste weg (Verhalten von vorher);
  // gefiltert erscheint sie mit dem Leer-Hinweis, sonst waere der Reset-Chip mit
  // der Sektion verschwunden und der Filter nicht mehr aufloesbar.
  const showTopSection = effectiveSegment !== null || topList.rows.length > 0;

  // Reine Flaeche, kein Text mehr: die Klassen fuer Zentrierung, Schriftgroesse
  // und Trunkierung sind mit der Beschriftung weggefallen (Iteration 4).
  const barClass = (seg: PriceSegment) =>
    ['cursor-pointer transition', effectiveSegment === seg ? BAR_ACTIVE_MARK : '']
      .filter(Boolean)
      .join(' ');

  /**
   * EINE Quelle fuer Chip-Beschriftung und aria-live-Ansage. Der Anteil kommt
   * aus `pctLabel`, nicht aus `pct`: bei gerundet 0 steht dort "<1", damit ein
   * sichtbares Segment nie als "0 %" beschriftet wird.
   */
  const chipLabel = (seg: PriceSegment) =>
    t(SEG_CHIP_KEY[seg], { pct: pctLabel(segCount(seg)) });

  return (
    <Card
      id="preis-kapitel"
      data-testid="price-benchmark-section"
      style={{ border: '1px solid hsl(var(--primary) / 0.08)', boxShadow: 'var(--shadow-sm)' }}
    >
      <CardContent className="pt-6 pb-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-[11px] font-bold tracking-[0.08em] text-primary uppercase">
            {t('priceBenchmark.chapterLabel', { total: chapterTotal })}
          </span>
          <span data-testid="price-coverage-badge" className={COVERAGE_BADGE}>
            {t('priceBenchmark.coverage', {
              bench: formatInteger(summary.bench_count, language),
              total: formatInteger(summary.product_total, language),
              pct: coveragePct,
            })}
          </span>
        </div>

        <h2 className="text-xl font-heading font-bold">
          {t('priceBenchmark.headline', {
            count: summary.above_count,
            formatted: formatInteger(summary.above_count, language),
          })}
        </h2>

        {/* 3-Segment-Balken (GMC-Referenzmuster). Seit Iteration 4 reine
            Flaechengrafik: die Anteile stehen in den Chips darunter, damit die
            Beschriftung nicht auf der roten bzw. gruenen Fuellung sitzen muss,
            wo sie 4,5:1 verfehlte. Die Segmente bleiben als Zeiger-Abkuerzung
            klickbar, sind aber vollstaendig aus dem Accessibility-Baum
            genommen, der Anteil steht ja im Chip. Die Spur traegt seit
            Iteration 5 eine Umrandung, weil das mittlere Segment ohne
            Beschriftung sonst mit der Karte verschmilzt (siehe BAR_TRACK). */}
        <div className="space-y-2">
          <div data-testid="seg-bar-track" className={BAR_TRACK}>
            {summary.above_count > 0 && (
              <div
                aria-hidden="true"
                data-testid="seg-bar-above"
                onClick={() => toggleSegment('above')}
                className={barClass('above')}
                style={{ width: `${widthPct(summary.above_count)}%`, background: 'hsl(var(--destructive))' }}
              />
            )}
            {summary.within_count > 0 && (
              <div
                aria-hidden="true"
                data-testid="seg-bar-within"
                onClick={() => toggleSegment('within')}
                className={barClass('within')}
                style={{ width: `${widthPct(summary.within_count)}%`, background: 'hsl(var(--muted))' }}
              />
            )}
            {summary.below_count > 0 && (
              <div
                aria-hidden="true"
                data-testid="seg-bar-below"
                onClick={() => toggleSegment('below')}
                className={barClass('below')}
                style={{ width: `${widthPct(summary.below_count)}%`, background: 'hsl(var(--success))' }}
              />
            )}
          </div>

          {/* Das eigentliche Bedienelement: echte Schaltflaechen mit
              Zielgroesse, und seit Iteration 4 der einzige Ort, an dem die
              drei Anteile als Text stehen. */}
          <div
            data-testid="seg-chips"
            role="group"
            aria-label={t('priceBenchmark.segFilterGroupLabel')}
            className="flex flex-wrap gap-2"
          >
            {SEGMENTS.filter((seg) => segCount(seg) > 0).map((seg) => (
              <button
                key={seg}
                type="button"
                ref={(el) => {
                  chipRefs.current[seg] = el;
                }}
                data-testid={`seg-${seg}`}
                aria-pressed={effectiveSegment === seg}
                onClick={() => toggleSegment(seg)}
                className={`${CHIP_BASE} ${effectiveSegment === seg ? CHIP_ACTIVE : CHIP_INACTIVE}`}
              >
                {chipLabel(seg)}
              </button>
            ))}
          </div>

          {/* Der Listentausch ist rein visuell und faende ohne diese Ansage
              nicht statt. Bewusst ein eigenes, verstecktes Statuselement und
              KEIN aria-live auf dem Listen-Container: das laese die ganze
              Liste vor.

              Ungefiltert wird GAR NICHTS gerendert statt eines leeren
              Katalogwerts: ein Katalogeintrag `""` kommt nur ueber die
              i18next-Option `returnEmptyString` unveraendert zurueck, und eine
              stille Umstellung dieser Option haette hier den Schluesselpfad
              vorlesen lassen. Das Element selbst bleibt im DOM, damit die
              Live-Region beim ersten Filterklick schon existiert. */}
          <p
            data-testid="seg-filter-status"
            role="status"
            aria-live="polite"
            className="sr-only"
          >
            {effectiveSegment !== null
              ? t('priceBenchmark.segFilterStatusOn', { segment: chipLabel(effectiveSegment) })
              : null}
          </p>

          <div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
            <span className="text-destructive-ink font-medium">
              {/* `amount`, nicht `count`: `count` ist bei i18next der reservierte
                  Pluraltreiber und muss eine Zahl sein — hier steht eine bereits
                  lokalisierte Zeichenkette. Der Key traegt keine Plural-Variante
                  (elliptische Statistikzeile, siehe productCountPlurals.test.ts). */}
              {t('priceBenchmark.aboveStats', {
                amount: formatInteger(summary.above_count, language),
                median: formatDecimal(summary.median_dev_pct ?? 0, language, 1),
                p90: formatDecimal(summary.p90_dev_pct ?? 0, language, 1),
              })}
            </span>
            <span className="text-success-ink font-medium">
              {t('priceBenchmark.belowStats', {
                amount: formatInteger(summary.below_count, language),
                avg: formatDecimal(summary.below_avg_dev_pct ?? 0, language, 1),
              })}
            </span>
          </div>
        </div>

        {/* Abweichung je Kategorie */}
        {summary.categories.length > 0 && (
          <div className="space-y-2 border-t pt-4" style={{ borderColor: 'hsl(var(--primary) / 0.08)' }}>
            <span className="text-sm font-semibold">{t('priceBenchmark.categoriesTitle')}</span>
            {summary.categories.map((cat) => (
              /* Prod-E2E 2026-08-22 (375 px): mit festen Spaltenbreiten in EINER
                 Zeile blieb der flex-1-Spur rechnerisch 0 px, alle Balken waren
                 unsichtbar. Unter `sm` bricht die Spur deshalb auf eine eigene
                 volle Zeile um (order-last + basis-full), der Prozentwert rueckt
                 per ml-auto an den rechten Rand; ab `sm` eine Zeile wie gehabt,
                 mit breiterem Label (w-40 statt w-32: "Arts & entertainment"
                 trunkierte am Desktop) und Mindestbreite fuer die Spur. */
              <div key={cat.label} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm sm:flex-nowrap">
                {/* Finding 3 (Review H1): defaultValue als Fallback — ein
                    kuenftiges, hier noch unbekanntes category_label rendert
                    den Rohwert statt des Key-Pfads. */}
                <span
                  data-testid={`cat-name-${cat.label}`}
                  title={t(`priceBenchmark.categoryLabel.${cat.label}`, { defaultValue: cat.label })}
                  // Nacharbeit A2O-18: mobil bricht der Name um statt zu kuerzen
                  // ("Arts & entertain..." auf 390 px); ab sm feste Spalte mit Kuerzung.
                  className="min-w-0 flex-1 break-words sm:w-40 sm:flex-none sm:truncate"
                >
                  {t(`priceBenchmark.categoryLabel.${cat.label}`, { defaultValue: cat.label })}
                </span>
                {/* xs ohne feste Breite + nowrap: "1.710 Prod." brach in w-16 auf zwei
                    Zeilen um (Prod-E2E 375 px, Runde 2); Platz ist da, weil die
                    Spur auf xs in der eigenen Zeile liegt. */}
                <span className="shrink-0 whitespace-nowrap text-[11px] text-muted-foreground tabular-nums sm:w-20">
                  {t('priceBenchmark.categoryCount', { count: cat.count, formatted: formatInteger(cat.count, language) })}
                </span>
                <div
                  data-testid={`cat-track-${cat.label}`}
                  className="relative order-last h-2.5 basis-full sm:order-none sm:basis-0 sm:flex-1 sm:min-w-[120px]"
                >
                  <div className="absolute top-0 h-full w-px" style={{ left: '50%', background: 'hsl(var(--border))' }} />
                  <div
                    data-testid={`cat-bar-${cat.label}`}
                    className="absolute top-0 h-full rounded-full"
                    style={
                      cat.avg_dev_pct >= 0
                        ? { left: '50%', width: `${clamp(cat.avg_dev_pct)}%`, background: devBg(cat.avg_dev_pct) }
                        : { right: '50%', width: `${clamp(cat.avg_dev_pct)}%`, background: devBg(cat.avg_dev_pct) }
                    }
                  />
                </div>
                <span className={`ml-auto w-14 shrink-0 text-right text-xs font-semibold tabular-nums sm:ml-0 ${devInk(cat.avg_dev_pct)}`}>
                  {cat.avg_dev_pct > 0 ? '+' : ''}{formatDecimal(cat.avg_dev_pct, language, 1)} %
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Top-Ausreisser mit Produkttitel + Echtpreisen */}
        {showTopSection && (
          <div className="space-y-1.5 border-t pt-4" style={{ borderColor: 'hsl(var(--primary) / 0.08)' }}>
            <div className="flex flex-wrap items-center gap-2">
              <span data-testid="price-top-title" className="text-sm font-semibold">{t(topList.titleKey)}</span>
              {/* Reset-Knopf auf derselben Basis wie die Segment-Chips statt
                  handgebauter Klassen: mit `px-2 py-0.5 text-[11px]` mass er
                  rund 23,6 px und blieb als einziges Bedienelement dieser Karte
                  unter den 24 px aus WCAG 2.2 SC 2.5.8. */}
              {effectiveSegment !== null && (
                <button
                  type="button"
                  data-testid="seg-filter-reset"
                  onClick={resetSegment}
                  className={`${CHIP_BASE} ${CHIP_INACTIVE}`}
                >
                  {t('priceBenchmark.segFilterReset')}
                </button>
              )}
            </div>
            {topList.rows.length === 0 ? (
              <p data-testid="price-top-empty" className="text-sm text-muted-foreground">
                {t('priceBenchmark.topEmpty')}
              </p>
            ) : (
              topList.rows.map((row, idx) => (
                <div
                  key={`${row.title}-${idx}`}
                  data-testid="price-top-row"
                  className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{row.title}</p>
                    <p className="text-[11px] text-muted-foreground tabular-nums">
                      {t('priceBenchmark.topPriceLine', {
                        price: priceLabel(row.price_micros, summary.currency, language),
                        benchmark: priceLabel(row.benchmark_price_micros, summary.currency, language),
                      })}
                    </p>
                  </div>
                  {/* Vorzeichen nur bei echter Ueberbietung; das Minus liefert
                      formatInteger selbst, und "+0 %" waere eine Ueberbietung,
                      die es nicht gibt. Farbe aus dem Segment der Liste, nicht
                      aus dem gerundeten Zeilenwert (siehe segInk). */}
                  <span
                    data-testid="price-top-dev"
                    className={`shrink-0 text-sm font-bold tabular-nums ${segInk(listSegment)}`}
                  >
                    {row.dev_pct > 0 ? '+' : ''}{formatInteger(row.dev_pct, language)} %
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Unter-Benchmark: die bisher unsichtbare Seite der Verteilung */}
        {summary.below_count > 0 && (
          <div
            data-testid="below-benchmark-card"
            className="rounded-xl px-4 py-3 text-sm"
            style={{ background: 'hsl(var(--success-light))' }}
          >
            <span className="text-success-ink">
              {t('priceBenchmark.belowCard', {
                count: summary.below_count,
                formatted: formatInteger(summary.below_count, language),
                avg: formatDecimal(summary.below_avg_dev_pct ?? 0, language, 1),
              })}
            </span>
          </div>
        )}

        <p className="text-[11px] leading-relaxed text-muted-foreground">
          {t('priceBenchmark.footnote', {
            // Finding 8 (Review H1): `numberLocale` statt Hand-Locale — die
            // Ternaere brach bei `de-CH`/`de-AT` (M4-Regression: gemischte
            // Formate in einer Karte).
            date: summary.pulled_at
              ? new Date(summary.pulled_at).toLocaleDateString(numberLocale(language))
              : '',
          })}
        </p>
      </CardContent>
    </Card>
  );
}
